/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 06-Aug-2026
 * @author      : Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Automation Plan Engine A2 — build-spec §3.4, §7 (D45/D52) — Provider Web
 * Purpose      : Provider plan-master authoring as a MODAL, matching the tenant "Connection plans"
 *                builder's UX — a drag-and-drop step sequence on the left, a per-kind action editor
 *                on the right, "Save action" / "Save & close". Provider data rules are preserved:
 *                a generic ROLE assignee (not a tenant user), master-catalog plan pickers, message
 *                template dropdowns, draft-only whole-set-replace save, no enrol control.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * A2 §3.4/§7     | 06-Aug-2026 | Aravindan      | Ported the full-page editor into a modal builder
 *                                                 with a drag-and-drop sequence, matching tenant UX.
 * PR 7871 #66453 | 11-Aug-2026 | Claude         | Plan name is read-only (no writer on the API) and
 *                                                 Next plan carries a "not saved yet" notice, so a
 *                                                 save no longer reports fields it discarded.
 * PR 7871 #66481 | 11-Aug-2026 | Claude         | Removed the in-component PlanPicker/RolePicker
 *                                                 that shadowed the module-scope pair; call sites
 *                                                 pass planOptions/catalogFailed/value/onChange.
 */

import React, { useCallback, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import HtmlPreview from '../communications/HtmlPreview';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import { ErrorState } from '../communications/AsyncStates';
import type { ApiResult } from './AutomationMasterService';
import { createDraft, fetchPlanMasterSteps, fetchPlanMasters, replaceSteps, updatePlanAttributes, fetchTemplateCatalog, TemplateOption } from './AutomationMasterService';
import {
  ACTION_TYPES,
  ActionKind,
  BRANCH_FIELDS,
  BRANCH_OPERATOR_LABEL,
  BranchOperator,
  BUILDER_ACTION_KINDS,
  CHECKLIST_ITEM_MAX_LENGTH,
  DEFAULT_STAGES,
  DELAY_UNITS,
  DelayUnit,
  LEAD_SOURCES,
  LEN_BODY_2000,
  LEN_NAME_200,
  LEN_NOTIFY_BODY_240,
  LEN_REF_128,
  LEN_STAGE_32,
  MAX_ALT_NUMBERS,
  MAX_CHECKLIST_ITEMS,
  PAY_DOCS,
  PlanMaster,
  PlanMasterStep,
  PlanMasterSummaryDTO,
  RECIPIENT_TYPES,
  STAGE_ACTIONS,
  StageAction,
  WAIT_ACTIONS,
  WAIT_MODES,
  WaitAction,
  WaitMode,
  actionLabel,
  anchorScheduledSteps,
  branchFieldDef,
  emptyPlanMaster,
  fromStepDTOs,
  newStep,
  planLevelFieldsArePersisted,
  splitAltNumbers,
  timingLabel,
  toStepDTOs,
  unknownKindSteps,
  VALUELESS_OPERATORS,
  wholeNumber,
} from './AutomationTypes';

/**
 * ============ HOW THIS DIFFERS FROM THE TENANT PlanBuilder, AND WHY ============
 *
 * Same screen shape — a step sequence, a per-kind editor — but a provider master is a template that
 * fans out to every tenant, not a plan running against one tenant's data, so one thing differs:
 *  1. PLAN PICKERS SELECT FROM THE MASTER CATALOG, NOT TENANT PLANS — fork time rebases a master
 *     uid onto the tenant's own fork uid.
 * NOT BUILT, on purpose: any ENROL control (tenant-scoped), and anchor/recurrence authoring.
 * REMOVED (2026-09-01, AU031): task/reminder role assignment (assignToKind/assignToRef) — no
 * executor was ever built for task/reminder (AutomationKindRouter refuses both, AU073) and this
 * screen never offered them as selectable step types, so the columns and this screen's role
 * picker were dead weight that had already caused one production defect.
 *
 * SAVE is a DRAFT-only whole-set replace (`updatePlanAttributes` then `replaceSteps`): whatever the
 * sequence holds on "Save & close" becomes the version's complete step set. A failed step READ
 * blocks saving, because an empty list and a failed read look identical and only one is safe.
 * ==============================================================================
 */

/** A caveat rendered where a field is authored, when saving will not persist it as expected. */
const FieldNotice: React.FC<{ children: React.ReactNode; severity?: 'info' | 'warning' }> = ({
  children,
  severity = 'warning',
}) => (
  <Alert severity={severity} sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { py: 0.75 } }}>
    <Typography variant="caption">{children}</Typography>
  </Alert>
);

interface Failure {
  message: string;
  httpStatus: number | null;
}

const describeFailure = (result: Exclude<ApiResult<unknown>, { kind: 'ok' }>): Failure => {
  switch (result.kind) {
    case 'forbidden':
      return {
        message: 'Refused — authoring a plan master needs provider-admin permission.',
        httpStatus: 403,
      };
    case 'unauthorized':
      return { message: 'Your session is not valid. Sign in again.', httpStatus: 401 };
    case 'notFound':
      return { message: result.message, httpStatus: 404 };
    default:
      return { message: result.message, httpStatus: result.status };
  }
};

const stepNumberList = (numbers: number[]): string =>
  numbers.map((n) => `step ${n}`).join(', ');

/**
 * The master-catalog picker — the provider-scoped substitute for a tenant-plan dropdown.
 *
 * AT MODULE SCOPE, NOT INSIDE THE COMPONENT. Declared in the builder's body it was a NEW component
 * type on every render, so React unmounted and remounted the whole subtree — every FormControl,
 * Select and FieldNotice — on each keystroke in the plan name, the description or any step field,
 * discarding Select popup and focus state. A branch step mounts four of these at once.
 */
const PlanPicker: React.FC<{
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  allowNone?: boolean;
  planOptions: PlanMasterSummaryDTO[];
  catalogFailed: boolean;
  /** Rendered under the picker when the chosen value cannot be persisted yet (plan-level fields). */
  notice?: React.ReactNode;
}> = ({ label, value, onChange, allowNone = true, planOptions, catalogFailed, notice }) => (
  <Box>
    <FormControl fullWidth size="small">
      <InputLabel id={`plan-picker-${label}`}>{label}</InputLabel>
      <Select
        labelId={`plan-picker-${label}`}
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(String(e.target.value))}
      >
        {allowNone && <MenuItem value="">— None —</MenuItem>}
        {planOptions.map((m) => (
          <MenuItem key={`${m.planMasterUid}-${m.version}`} value={m.planMasterUid}>
            {m.name ?? m.planMasterUid} (v{m.version})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    {notice}
    {catalogFailed && (
      <FieldNotice>
        The plan catalog could not be loaded, so this list is empty — that is not a statement that
        no published plans exist. Reload the page to try again.
      </FieldNotice>
    )}
    {!catalogFailed && planOptions.length === 0 && (
      <FieldNotice severity="info">
        No other published plan master exists yet. Only published versions can be targeted — a
        draft is not forkable by any tenant.
      </FieldNotice>
    )}
  </Box>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" color="primary" sx={{ display: 'block', mb: 1 }}>
    {children}
  </Typography>
);

/** What the list screen hands the builder to open one (planMasterUid, version) draft. */
export interface PlanMasterBuilderTarget {
  planMasterUid: string;
  version: number;
  status: string;
  name: string;
  description?: string | null;
  /** Set by the "New plan" flow — the draft provably has zero steps, so the step read is skipped. */
  justCreated?: boolean;
  /**
   * CREATE MODE — no master exists yet. The whole plan (name, plan-level attributes and steps) is
   * authored in this one modal and CREATED on "Save & close": `createDraft` mints the DRAFT, then a
   * single `replaceSteps` writes whatever steps were added. This unifies the old two-screen flow (a
   * name-only "New plan master" dialog, then the editor) into one builder, matching the tenant UX.
   */
  createMode?: boolean;
  /** Plan-level values to pre-fill in EDIT mode (from the list row) so a save does not blank them. */
  leadSource?: string | null;
  entryStage?: string | null;
  nextPlanMasterUid?: string | null;
}

interface PlanMasterBuilderProps {
  target: PlanMasterBuilderTarget;
  onClose: () => void;
  /** severity defaults to 'success'; a partial outcome (e.g. created but steps failed) passes
   *  'warning' so the list banner does not render a failure in the green success channel (finding #4). */
  onSaved: (message: string, severity?: 'success' | 'warning') => void;
}

/**
 * SYSTEM templates are omitted from the step dropdowns — and ONLY from the dropdowns.
 *
 * <p>A SYSTEM template is fired by application code at a business event (the contract emails), so
 * binding one to a plan step would mean two different things sending the same message, to possibly
 * different audiences, with no coordination between them. `pickerCatalog` deliberately returns
 * SYSTEM rows so the read-only Templates list can display them — a provider has every reason to SEE
 * which messages the platform sends — which is exactly why the exclusion has to live here, at the
 * point of selection, rather than in the service both consumers share.
 *
 * <p>This is a CONVENIENCE, not the control. `AutomationPlanMasterService.replaceSteps` refuses a
 * SYSTEM binding with AU133 regardless of what any client sends, because `AutomationPlanService
 * .fork` copies a master's templateMasterUid into every tenant fork with no re-check. Mirrors the
 * tenant side's `isSelectable` in useAutomationPlans.ts.
 *
 * <p>A null/absent kind stays selectable: rows written before the column existed read back null,
 * and treating those as SYSTEM would empty the dropdown of every legacy template.
 */
const isSelectableTemplate = (t: TemplateOption): boolean => t.templateKind !== 'SYSTEM';

export const PlanMasterBuilder: React.FC<PlanMasterBuilderProps> = ({ target, onClose, onSaved }) => {
  const { accessToken } = useOidcAccessToken();
  const createMode = target.createMode === true;
  // In create mode there is no master yet — planMasterUid is empty, which every downstream check
  // (`!uid` skips the step read, `!!plan.planMasterUid` gates the edit-save path) already treats as
  // "nothing to load / not yet persisted".
  const uid = target.planMasterUid;
  const version = target.version;
  const justCreated = target.justCreated === true;

  const [plan, setPlan] = useState<PlanMaster>(() => ({
    ...emptyPlanMaster(),
    planMasterUid: uid || undefined,
    version: target.version,
    status: (target.status as PlanMaster['status']) ?? 'DRAFT',
    name: target.name ?? '',
    description: target.description ?? '',
    // Pre-fill plan-level values from the list row (edit mode) so the controls show the real current
    // values and a save re-sends them rather than blanking. Blank is fine in create mode.
    leadSource: target.leadSource ?? '',
    entryStage: target.entryStage ?? emptyPlanMaster().entryStage,
    nextPlanMasterUid: target.nextPlanMasterUid ?? '',
  }));
  const [selIdx, setSelIdx] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  // Master catalog for every plan-valued picker — PUBLISHED versions only (a draft is not forkable).
  const [catalog, setCatalog] = useState<PlanMasterSummaryDTO[] | null>(null);
  const [catalogFailed, setCatalogFailed] = useState(false);

  // Message-template catalogs for the step's Email/SMS pickers — APPROVED/PUBLISHED, per channel.
  const [emailTemplates, setEmailTemplates] = useState<TemplateOption[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<TemplateOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  // A FAILED template-catalog load must not read as "no templates" — otherwise the picker shows only
  // "— None —" and a saved template renders "(not in catalog)", both actively false when the catalog
  // merely failed to load. Mirrors the plan-catalog `catalogFailed` handling below (review finding #3).
  const [templateCatalogFailed, setTemplateCatalogFailed] = useState(false);
  React.useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    setTemplateCatalogFailed(false);
    Promise.all([
      fetchTemplateCatalog('EMAIL', accessToken).then((r) => {
        if (cancelled) return;
        if (r.kind === 'ok') setEmailTemplates(r.data.filter(isSelectableTemplate));
        else setTemplateCatalogFailed(true);
      }),
      fetchTemplateCatalog('SMS', accessToken).then((r) => {
        if (cancelled) return;
        if (r.kind === 'ok') setSmsTemplates(r.data.filter(isSelectableTemplate));
        else setTemplateCatalogFailed(true);
      }),
    ]).finally(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; };
  }, [accessToken]);
  React.useEffect(() => {
    let cancelled = false;
    fetchPlanMasters(accessToken).then((result) => {
      if (cancelled) return;
      if (result.kind === 'ok') {
        setCatalog(result.data.filter((m) => m.status === 'PUBLISHED' && m.planMasterUid !== uid));
      } else {
        setCatalogFailed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, uid]);

  /**
   * LOAD the existing steps. A failed read is the load-bearing case: `PUT …/steps` is a whole-set
   * replace, so an empty list and a failed read look identical and saving the empty one DELETES the
   * version's steps — a failed read therefore puts the screen in `failed` and `canSave` refuses.
   */
  const [stepsLoad, setStepsLoad] = useState<'skipped' | 'loading' | 'loaded' | 'failed'>(
    justCreated || !uid ? 'skipped' : 'loading',
  );
  const [stepsFailure, setStepsFailure] = useState<Failure | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  /**
   * What a save would rewrite in the steps just read — see "WHAT A ROUND TRIP THROUGH THIS EDITOR
   * CHANGES" in AutomationTypes. Computed against the READ DTOs, not the mapped steps: the mapping
   * is exactly what erases the evidence (an anchor mode presents as "delayed", a USER assignee is
   * dropped), so after `fromStepDTOs` there is nothing left to detect.
   */
  const [rewrites, setRewrites] = useState<{ anchor: number[]; unknown: number[] }>(
    { anchor: [], unknown: [] },
  );

  React.useEffect(() => {
    if (justCreated || !uid) return undefined;
    let cancelled = false;
    setStepsLoad('loading');
    setStepsFailure(null);
    fetchPlanMasterSteps(uid, version, accessToken).then((result) => {
      if (cancelled) return;
      if (result.kind !== 'ok') {
        setStepsFailure(describeFailure(result));
        setStepsLoad('failed');
        return;
      }
      setRewrites({
        anchor: anchorScheduledSteps(result.data),
        unknown: unknownKindSteps(result.data),
      });
      setPlan((p) => ({ ...p, steps: fromStepDTOs(result.data) }));
      setSelIdx(null);
      setStepsLoad('loaded');
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, uid, version, justCreated, reloadToken]);

  const draft = selIdx != null ? plan.steps[selIdx] : null;

  const setPlanField = <K extends keyof PlanMaster>(key: K, value: PlanMaster[K]) =>
    setPlan((p) => ({ ...p, [key]: value }));

  const patchDraft = useCallback(
    (patch: Partial<PlanMasterStep>) =>
      setPlan((p) => ({
        ...p,
        steps: p.steps.map((s, i) => (i === selIdx ? { ...s, ...patch } : s)),
      })),
    [selIdx],
  );

  const setDraft = <K extends keyof PlanMasterStep>(key: K, value: PlanMasterStep[K]) =>
    patchDraft({ [key]: value } as Partial<PlanMasterStep>);

  const addStep = () => {
    setPlan((p) => ({ ...p, steps: [...p.steps, newStep(p.steps.length + 1)] }));
    setSelIdx(plan.steps.length);
  };

  const deleteStep = (idx: number) => {
    setPlan((p) => ({
      ...p,
      steps: p.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepOrder: i + 1 })),
    }));
    setSelIdx((cur) => (cur == null ? null : cur === idx ? null : cur > idx ? cur - 1 : cur));
  };

  // ── Native HTML5 drag-and-drop reordering (matches the tenant builder's sequence list). ──
  const dragFrom = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const dropStep = (to: number) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    setDragOver(null);
    if (from == null || from === to) return;
    setPlan((p) => {
      const arr = [...p.steps];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return { ...p, steps: arr.map((s, i) => ({ ...s, stepOrder: i + 1 })) };
    });
    setSelIdx(to);
  };

  // ── save-blocking validation, mirroring AutomationStepConfigValidator's refusals ──
  const stepProblem = (step: PlanMasterStep): string | null => {
    switch (step.kind) {
      case 'stage':
        if ((step.stageAction ?? 'stage') === 'stage' && !(step.targetStage ?? '').trim()) {
          return 'A “change deal stage” step needs a target stage.';
        }
        if (step.stageAction === 'plan' && !(step.targetPlan ?? '').trim()) {
          return 'A “move to plan” step needs a target plan.';
        }
        return null;
      case 'branch': {
        const def = branchFieldDef((step.condition?.field ?? '').trim());
        const operator = step.condition?.operator as BranchOperator | undefined;
        if (!def) return 'A branch step needs a condition field.';
        if (!operator || !def.operators.includes(operator)) {
          return 'A branch step needs an operator valid for its field.';
        }
        if (!VALUELESS_OPERATORS.includes(operator) && (step.condition?.value ?? '').trim() === '') {
          return 'This operator needs a comparison value.';
        }
        return null;
      }
      case 'pay':
        return wholeNumber(step.invoiceId) !== null && wholeNumber(step.invoiceId)! > 0
          ? null
          : 'A payment step needs a positive invoice ID.';
      case 'moveplan':
        return (step.targetPlan ?? '').trim() ? null : 'A “move to another plan” step needs a target plan.';
      case 'wait': {
        const mode = step.waitMode ?? 'days';
        if (mode === 'days') {
          const days = wholeNumber(step.waitDays);
          return days !== null && days > 0 ? null : 'A “wait N days” step needs a positive number of days.';
        }
        if (mode === 'response') {
          if (!(step.waitTrigger ?? '').trim()) return 'Waiting for a response needs a trigger.';
          const num = wholeNumber(step.waitWindowNum);
          if (num === null || num <= 0) return 'Waiting for a response needs a positive window.';
          if (!(step.waitWindowUnit ?? '').trim()) return 'Waiting for a response needs a window unit.';
        }
        return null;
      }
      case 'sms':
        return splitAltNumbers(step.altNumbers).length > MAX_ALT_NUMBERS
          ? `At most ${MAX_ALT_NUMBERS} alternate numbers.`
          : null;
      default:
        return null;
    }
  };

  const problems = plan.steps
    .map((s, i) => ({ index: i, problem: stepProblem(s) }))
    .filter((p): p is { index: number; problem: string } => p.problem != null);

  const canSave =
    !busy &&
    stepsLoad !== 'loading' &&
    stepsLoad !== 'failed' &&
    problems.length === 0 &&
    // CREATE: a name is the only hard requirement (steps may be added now or on a later Edit steps,
    // exactly as the old name-only dialog let you create a stepless draft). EDIT: the version must
    // already exist and hold at least one step (a whole-set replace to an empty list wipes steps).
    (createMode ? plan.name.trim() !== '' : plan.steps.length > 0 && !!plan.planMasterUid);

  /** The plan-level attributes both paths persist — blank values become null so a save leaves an
   *  unset field unchanged rather than authoring an empty string (mirrors applyIfPresent server-side). */
  const planLevelAttrs = () => ({
    description: plan.description ?? '',
    leadSource: plan.leadSource.trim() || null,
    entryStage: plan.entryStage.trim() || null,
    nextPlanMasterUid: plan.nextPlanMasterUid.trim() || null,
  });

  const save = async () => {
    setBusy(true);
    setFailure(null);

    // ── CREATE MODE ── mint the DRAFT (name + plan-level attrs in one POST), then write its steps.
    if (createMode) {
      if (plan.name.trim() === '') {
        setBusy(false);
        return;
      }
      const created = await createDraft(
        { name: plan.name.trim(), ...planLevelAttrs() },
        accessToken,
      );
      if (created.kind !== 'ok') {
        setBusy(false);
        setFailure(describeFailure(created));
        return;
      }
      // Steps are optional on create; write them only when some were authored (a whole-set replace
      // of an empty array on a brand-new draft is a no-op, but skipping the call is clearer).
      if (plan.steps.length > 0) {
        const stepsRes = await replaceSteps(
          created.data.planMasterUid,
          created.data.version,
          toStepDTOs(plan.steps),
          accessToken,
        );
        setBusy(false);
        if (stepsRes.kind !== 'ok') {
          // The draft was created but its steps did not save — reported precisely as a WARNING (not
          // the green success channel), and the list still reloads so the new stepless draft is
          // visible to finish later with "Edit steps".
          onSaved(
            `Created draft “${plan.name.trim()}” (v${created.data.version}), but saving its steps failed: ${describeFailure(stepsRes).message} Open it with “Edit steps” to retry.`,
            'warning',
          );
          onClose();
          return;
        }
      } else {
        setBusy(false);
      }
      onSaved(
        `Created draft “${plan.name.trim()}” (v${created.data.version})` +
          (plan.steps.length > 0
            ? ` with ${plan.steps.length} step${plan.steps.length === 1 ? '' : 's'}`
            : ' with no steps yet') +
          '. Publish the version to make it available to tenants.',
      );
      onClose();
      return;
    }

    // ── EDIT MODE ── existing draft: persist plan-level attrs, then replace its step set.
    if (!plan.planMasterUid) return;
    const attrsResult = await updatePlanAttributes(
      plan.planMasterUid,
      plan.version,
      // Full plan-level set now persists (the PUT was widened 2026-08-13). Lead source, trigger
      // (entryStage) and Next plan all save now — nextPlanMasterUid was the field that used to be
      // lost on refresh (the old "Not saved yet" warning).
      planLevelAttrs(),
      accessToken,
    );
    if (attrsResult.kind !== 'ok') {
      setBusy(false);
      setFailure(describeFailure(attrsResult));
      return;
    }
    const result = await replaceSteps(
      plan.planMasterUid,
      plan.version,
      toStepDTOs(plan.steps),
      accessToken,
    );
    setBusy(false);
    if (result.kind === 'ok') {
      onSaved(
        `Saved the plan settings and ${plan.steps.length} step${plan.steps.length === 1 ? '' : 's'} on v${plan.version}. Publish the version to make it available to tenants.`,
      );
      onClose();
      return;
    }
    setFailure(describeFailure(result));
  };

  const planOptions = catalog ?? [];

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" data-testid="plan-master-builder">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ flexGrow: 1 }}>
            {/* EDITABLE in create mode (the name is written by createDraft on Save & close), READ-ONLY
                in edit mode (updateDraftAttributes reads no name, so an editable field would report
                success and silently discard a rename — renaming an existing plan is not supported). */}
            <TextField
              variant="standard"
              fullWidth
              autoFocus={createMode}
              placeholder="Plan name"
              value={plan.name}
              onChange={createMode ? (e) => setPlanField('name', e.target.value) : undefined}
              error={createMode && plan.name.trim() === ''}
              inputProps={{ maxLength: LEN_NAME_200, 'data-testid': 'plan-name-input' }}
              InputProps={{ readOnly: !createMode, sx: { fontSize: '1.25rem', fontWeight: 600 } }}
            />
            {/* Edit mode shows ONLY the plan name — no version/status or helper captions (removed at
                the user's request). Create mode keeps the two hints so a first-time author knows the
                plan is a draft and is created on Save & close. */}
            {createMode && (
              <>
                <Typography variant="caption" color="text.secondary">
                  New plan · Draft (not forkable until published)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The plan is created when you Save &amp; close — name it, add its steps, then publish it.
                </Typography>
              </>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Close" data-testid="builder-close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {/* Plan-level fields. DESCRIPTION is the ONE that persists — it is the only key the
            plan-level PUT binds. NEXT PLAN is modelled and authored but has no writer, so it
            carries its caveat at the point of authoring rather than reporting a false save. */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={plan.description}
                onChange={(e) => setPlanField('description', e.target.value)}
                inputProps={{ 'data-testid': 'plan-description-input' }}
              />
            </Grid>
            {/* Lead source + Trigger (entryStage) — the tenant builder authors both; parity was the
                point of the unified builder. Both persist via updatePlanAttributes / createDraft.
                Lead source blank = "Any"; Trigger is the CRM entry stage the master fires on. */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="plan-lead-source">Lead source</InputLabel>
                <Select
                  labelId="plan-lead-source"
                  label="Lead source"
                  value={plan.leadSource}
                  onChange={(e) => setPlanField('leadSource', String(e.target.value))}
                  inputProps={{ 'data-testid': 'plan-lead-source-select' }}
                >
                  <MenuItem value="">— Any —</MenuItem>
                  {LEAD_SOURCES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="plan-trigger-stage">Trigger (entry stage)</InputLabel>
                <Select
                  labelId="plan-trigger-stage"
                  label="Trigger (entry stage)"
                  value={plan.entryStage}
                  onChange={(e) => setPlanField('entryStage', String(e.target.value))}
                  inputProps={{ 'data-testid': 'plan-trigger-stage-select' }}
                >
                  {DEFAULT_STAGES.map((s) => (
                    <MenuItem key={s.key} value={s.key}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <PlanPicker
                label="Next plan"
                value={plan.nextPlanMasterUid}
                onChange={(v) => setPlanField('nextPlanMasterUid', v)}
                planOptions={planOptions}
                catalogFailed={catalogFailed}
                notice={
                  planLevelFieldsArePersisted ? undefined : (
                    <FieldNotice>
                      <span data-testid="next-plan-not-saved">
                        Not saved yet — the plan-level PUT carries the description only, so a value
                        chosen here is lost on the next refresh. Flip
                        <code> planLevelFieldsArePersisted </code>
                        when the endpoint widens.
                      </span>
                    </FieldNotice>
                  )
                }
              />
            </Grid>
          </Grid>
        </Paper>

        {stepsLoad === 'failed' && stepsFailure && (
          <Box data-testid="steps-load-failed" sx={{ mb: 2 }}>
            <ErrorState
              message={`${stepsFailure.message} Saving is blocked — the steps already on v${version} could not be read, and saving what is on screen would replace them.`}
              httpStatus={stepsFailure.httpStatus}
              onRetry={() => setReloadToken((t) => t + 1)}
            />
          </Box>
        )}

        {(rewrites.anchor.length > 0 || rewrites.unknown.length > 0) && (
          <Box data-testid="step-rewrite-warning" sx={{ mb: 2 }}>
            <Alert severity="warning">
              <AlertTitle>Saving will rewrite step data this editor cannot author</AlertTitle>
              <Typography variant="caption" component="div">
                “Save & close” replaces the whole step set on v{version} with what is on screen, and
                every tenant that forks this master gets the result.
              </Typography>
              {rewrites.anchor.length > 0 && (
                <Typography variant="caption" component="div">
                  Anchor-scheduled: {stepNumberList(rewrites.anchor)} — saving downgrades the
                  schedule to “after the previous step”.
                </Typography>
              )}
              {rewrites.unknown.length > 0 && (
                <Typography variant="caption" component="div">
                  Action type this editor has no form for: {stepNumberList(rewrites.unknown)} —
                  saving discards the configuration held on those steps.
                </Typography>
              )}
            </Alert>
          </Box>
        )}

        {failure && <Box sx={{ mb: 2 }}><ErrorState message={failure.message} httpStatus={failure.httpStatus} /></Box>}

        <Grid container spacing={2}>
          {/* Sequence — drag-and-drop step list */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined">
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2">Sequence ({plan.steps.length})</Typography>
                <Button
                  size="small"
                  variant="contained"
                  disabled={stepsLoad === 'loading'}
                  onClick={addStep}
                  data-testid="add-step"
                >
                  Add action
                </Button>
              </Stack>
              <Divider />
              <List dense disablePadding>
                {plan.steps.map((s, i) => {
                  const problem = stepProblem(s);
                  return (
                    <ListItemButton
                      key={s.id}
                      selected={selIdx === i}
                      onClick={() => setSelIdx(i)}
                      data-testid={`step-row-${i}`}
                      draggable
                      onDragStart={() => {
                        dragFrom.current = i;
                      }}
                      onDragEnter={() => setDragOver(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => dropStep(i)}
                      onDragEnd={() => {
                        dragFrom.current = null;
                        setDragOver(null);
                      }}
                      sx={dragOver === i ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' } : undefined}
                    >
                      <DragIndicatorIcon
                        fontSize="small"
                        sx={{ color: 'text.disabled', mr: 0.5, cursor: 'grab' }}
                        titleAccess="Drag to reorder"
                      />
                      <ListItemText
                        primary={`${i + 1}. ${s.label || actionLabel(s.kind)}`}
                        secondary={`${actionLabel(s.kind)} · ${timingLabel(s)}${problem ? ` · ${problem}` : ''}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: problem ? 'error' : 'text.secondary',
                        }}
                      />
                      <IconButton
                        size="small"
                        title="Remove step"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteStep(i);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  );
                })}
              </List>
              {plan.steps.length === 0 && (
                <Box sx={{ p: 2.5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {stepsLoad === 'loading'
                      ? 'Loading this version’s steps…'
                      : stepsLoad === 'failed'
                        ? 'The steps on this version could not be read — this is not an empty plan. Retry above.'
                        : 'No steps yet. A plan with no steps does nothing when a tenant forks it.'}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Step editor */}
          <Grid item xs={12} md={8}>
            {!draft && (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="subtitle1" color="text.secondary">
                  Select a step to edit
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Pick a step on the left, or add one to configure its content, timing and assignee.
                </Typography>
              </Paper>
            )}

            {draft && (
              <Stack spacing={2}>
                <Typography variant="h6" data-testid="step-heading">
                  Step {selIdx != null ? selIdx + 1 : ''}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionTitle>Action</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="kind">Action type</InputLabel>
                        <Select
                          labelId="kind"
                          label="Action type"
                          value={draft.kind}
                          onChange={(e) => setDraft('kind', e.target.value as ActionKind)}
                          inputProps={{ 'data-testid': 'kind-select' }}
                        >
                          {[
                            ...BUILDER_ACTION_KINDS,
                            ...(BUILDER_ACTION_KINDS.includes(draft.kind) ? [] : [draft.kind]),
                          ].map((k) => {
                            const a = ACTION_TYPES.find((x) => x.k === k);
                            return (
                              <MenuItem key={k} value={k}>
                                {a?.label ?? k}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Step name"
                        value={draft.label}
                        onChange={(e) => setDraft('label', e.target.value)}
                        inputProps={{ maxLength: LEN_NAME_200, 'data-testid': 'label-input' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={draft.addNotifications}
                            onChange={(e) => setDraft('addNotifications', e.target.checked)}
                          />
                        }
                        label="Add notifications when this step fires"
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionTitle>Delivery</SectionTitle>
                  <RadioGroup
                    row
                    value={draft.deliveryMode}
                    onChange={(e) => setDraft('deliveryMode', e.target.value as PlanMasterStep['deliveryMode'])}
                  >
                    <FormControlLabel value="immediate" control={<Radio />} label="Immediate" />
                    <FormControlLabel value="delayed" control={<Radio />} label="Delayed" />
                  </RadioGroup>
                  {draft.deliveryMode === 'delayed' && (
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
                      <TextField
                        size="small"
                        type="number"
                        label="Delay"
                        value={draft.delayNum}
                        onChange={(e) => setDraft('delayNum', e.target.value)}
                        sx={{ width: 110 }}
                        inputProps={{ min: 0, step: 1 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel id="delay-unit">Unit</InputLabel>
                        <Select
                          labelId="delay-unit"
                          label="Unit"
                          value={draft.delayUnit}
                          onChange={(e) => setDraft('delayUnit', e.target.value as DelayUnit)}
                        >
                          {DELAY_UNITS.map((u) => (
                            <MenuItem key={u} value={u}>
                              {u}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {(draft.delayUnit === 'Days' || draft.delayUnit === 'Weeks') && (
                        <Box>
                          <TextField
                            size="small"
                            type="time"
                            label="Time of day"
                            value={draft.delayTime ?? ''}
                            onChange={(e) => setDraft('delayTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Box>
                      )}
                    </Stack>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionTitle>{actionLabel(draft.kind)} configuration</SectionTitle>

                  {/* A failed catalog load is NOT "no templates" — say so where a template is picked,
                      so an empty dropdown / "(not in catalog)" is not read as the truth (finding #3). */}
                  {templateCatalogFailed && (draft.kind === 'sms' || draft.kind === 'email') && (
                    <FieldNotice>
                      The message-template catalog could not be loaded, so this list is empty and a
                      saved template may show “(not in catalog)” — that is not a statement that no
                      templates exist. Reload the page to try again.
                    </FieldNotice>
                  )}

                  {draft.kind === 'sms' && (
                    <Stack spacing={2}>
                      <Box>
                        <FormControl fullWidth size="small">
                          <InputLabel id="sms-template">SMS template</InputLabel>
                          <Select
                            labelId="sms-template"
                            label="SMS template"
                            value={draft.smsTemplate ?? ''}
                            onChange={(e) => setDraft('smsTemplate', e.target.value)}
                            inputProps={{ 'data-testid': 'sms-template-select' }}
                          >
                            <MenuItem value="">— None —</MenuItem>
                            {smsTemplates.map((t) => (
                              <MenuItem key={t.templateMasterUid} value={t.templateMasterUid}>
                                {t.name}
                              </MenuItem>
                            ))}
                            {draft.smsTemplate
                              && !smsTemplates.some((t) => t.templateMasterUid === draft.smsTemplate) && (
                                <MenuItem value={draft.smsTemplate}>
                                  {catalogLoading ? 'Loading…' : `${draft.smsTemplate} (not in catalog)`}
                                </MenuItem>
                              )}
                          </Select>
                        </FormControl>
                      </Box>
                      {(() => {
                        const selected = smsTemplates.find((t) => t.templateMasterUid === draft.smsTemplate);
                        if (!draft.smsTemplate) return null;
                        return (
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Message (from template)"
                            value={selected?.body ?? (catalogLoading ? 'Loading…' : '')}
                            InputProps={{ readOnly: true }}
                            helperText="Loaded from the selected template — edit the template itself to change it."
                          />
                        );
                      })()}
                    </Stack>
                  )}

                  {draft.kind === 'email' && (
                    <Stack spacing={2}>
                      <Box>
                        <FormControl fullWidth size="small">
                          <InputLabel id="email-template">Email template</InputLabel>
                          <Select
                            labelId="email-template"
                            label="Email template"
                            value={draft.template ?? ''}
                            onChange={(e) => setDraft('template', e.target.value)}
                            inputProps={{ 'data-testid': 'email-template-select' }}
                          >
                            <MenuItem value="">— None —</MenuItem>
                            {emailTemplates.map((t) => (
                              <MenuItem key={t.templateMasterUid} value={t.templateMasterUid}>
                                {t.name}
                              </MenuItem>
                            ))}
                            {draft.template
                              && !emailTemplates.some((t) => t.templateMasterUid === draft.template) && (
                                <MenuItem value={draft.template}>
                                  {catalogLoading ? 'Loading…' : `${draft.template} (not in catalog)`}
                                </MenuItem>
                              )}
                          </Select>
                        </FormControl>
                      </Box>
                      {(() => {
                        const selected = emailTemplates.find((t) => t.templateMasterUid === draft.template);
                        if (!draft.template) return null;
                        return (
                          <>
                            {selected?.subject ? (
                              <TextField
                                fullWidth
                                size="small"
                                label="Subject (from template)"
                                value={selected.subject}
                                InputProps={{ readOnly: true }}
                              />
                            ) : null}
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.5 }}
                              >
                                Body preview (from template)
                              </Typography>
                              <HtmlPreview
                                html={selected?.body ?? ''}
                                testId="step-email-body-preview"
                                minHeight={220}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5 }}
                              >
                                {catalogLoading
                                  ? 'Loading…'
                                  : 'Loaded from the selected template — edit the template itself to change it.'}
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                    </Stack>
                  )}

                  {draft.kind === 'task' && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Notes"
                        value={draft.notes ?? ''}
                        onChange={(e) => setDraft('notes', e.target.value)}
                        inputProps={{ maxLength: LEN_BODY_2000 }}
                      />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Checklist ({(draft.checklistItems ?? []).length}/{MAX_CHECKLIST_ITEMS})
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 0.5 }}>
                          {(draft.checklistItems ?? []).map((item, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Stack key={`cl-${i}`} direction="row" spacing={1}>
                              <TextField
                                fullWidth
                                size="small"
                                value={item}
                                onChange={(e) =>
                                  patchDraft({
                                    checklistItems: (draft.checklistItems ?? []).map((c, j) =>
                                      j === i ? e.target.value : c,
                                    ),
                                  })
                                }
                                inputProps={{ maxLength: CHECKLIST_ITEM_MAX_LENGTH }}
                              />
                              <IconButton
                                size="small"
                                title={`Remove item ${i + 1}`}
                                onClick={() =>
                                  patchDraft({
                                    checklistItems: (draft.checklistItems ?? []).filter((_, j) => j !== i),
                                  })
                                }
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ))}
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ alignSelf: 'flex-start' }}
                            disabled={(draft.checklistItems ?? []).length >= MAX_CHECKLIST_ITEMS}
                            onClick={() =>
                              patchDraft({ checklistItems: [...(draft.checklistItems ?? []), ''] })
                            }
                          >
                            Add checklist item
                          </Button>
                        </Stack>
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!draft.autoStart}
                            onChange={(e) => setDraft('autoStart', e.target.checked)}
                          />
                        }
                        label="Start the task automatically"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!draft.autoComplete}
                            onChange={(e) => setDraft('autoComplete', e.target.checked)}
                          />
                        }
                        label="Complete the task automatically"
                      />
                    </Stack>
                  )}

                  {draft.kind === 'reminder' && (
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Notes"
                      value={draft.notes ?? ''}
                      onChange={(e) => setDraft('notes', e.target.value)}
                      inputProps={{ maxLength: LEN_BODY_2000 }}
                    />
                  )}

                  {draft.kind === 'stage' && (
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="stage-action">What this step does</InputLabel>
                        <Select
                          labelId="stage-action"
                          label="What this step does"
                          value={draft.stageAction ?? 'stage'}
                          onChange={(e) => setDraft('stageAction', e.target.value as StageAction)}
                        >
                          {STAGE_ACTIONS.map((a) => (
                            <MenuItem key={a} value={a}>
                              {a === 'stage' ? 'Change the deal stage' : 'Move onto another plan'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {(draft.stageAction ?? 'stage') === 'stage' ? (
                        <FormControl fullWidth size="small">
                          <InputLabel id="target-stage">Target stage</InputLabel>
                          <Select
                            labelId="target-stage"
                            label="Target stage"
                            value={draft.targetStage ?? ''}
                            onChange={(e) => setDraft('targetStage', String(e.target.value))}
                          >
                            {DEFAULT_STAGES.map((s) => (
                              <MenuItem key={s.key} value={s.key}>
                                {s.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <PlanPicker
                          label="Target plan (master)"
                          value={draft.targetPlan}
                          onChange={(v) => setDraft('targetPlan', v)}
                          allowNone={false}
                          planOptions={planOptions}
                          catalogFailed={catalogFailed}
                        />
                      )}
                    </Stack>
                  )}

                  {draft.kind === 'branch' && (
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="cond-field">Condition field</InputLabel>
                        <Select
                          labelId="cond-field"
                          label="Condition field"
                          value={draft.condition?.field ?? ''}
                          onChange={(e) =>
                            patchDraft({ condition: { field: String(e.target.value) } })
                          }
                        >
                          {BRANCH_FIELDS.map((f) => (
                            <MenuItem key={f.field} value={f.field}>
                              {f.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {(() => {
                        const def = branchFieldDef(draft.condition?.field);
                        const operator = draft.condition?.operator as BranchOperator | undefined;
                        const needsValue =
                          !!operator && !VALUELESS_OPERATORS.includes(operator);
                        return (
                          <>
                            <FormControl fullWidth size="small" disabled={!def}>
                              <InputLabel id="cond-op">Operator</InputLabel>
                              <Select
                                labelId="cond-op"
                                label="Operator"
                                value={operator ?? ''}
                                onChange={(e) =>
                                  patchDraft({
                                    condition: {
                                      ...draft.condition,
                                      operator: e.target.value as BranchOperator,
                                    },
                                  })
                                }
                              >
                                {(def?.operators ?? []).map((o) => (
                                  <MenuItem key={o} value={o}>
                                    {BRANCH_OPERATOR_LABEL[o]}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {needsValue && def?.valueKind === 'stage' && (
                              <FormControl fullWidth size="small">
                                <InputLabel id="cond-val-stage">Value</InputLabel>
                                <Select
                                  labelId="cond-val-stage"
                                  label="Value"
                                  value={draft.condition?.value ?? ''}
                                  onChange={(e) =>
                                    patchDraft({
                                      condition: { ...draft.condition, value: String(e.target.value) },
                                    })
                                  }
                                >
                                  {DEFAULT_STAGES.map((s) => (
                                    <MenuItem key={s.key} value={s.key}>
                                      {s.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            {needsValue && def?.valueKind === 'recipientType' && (
                              <FormControl fullWidth size="small">
                                <InputLabel id="cond-val-rt">Value</InputLabel>
                                <Select
                                  labelId="cond-val-rt"
                                  label="Value"
                                  value={draft.condition?.value ?? ''}
                                  onChange={(e) =>
                                    patchDraft({
                                      condition: { ...draft.condition, value: String(e.target.value) },
                                    })
                                  }
                                >
                                  {RECIPIENT_TYPES.map((r) => (
                                    <MenuItem key={r} value={r}>
                                      {r}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            {needsValue && def?.valueKind === 'number' && (
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Value"
                                value={draft.condition?.value ?? ''}
                                onChange={(e) =>
                                  patchDraft({
                                    condition: { ...draft.condition, value: e.target.value },
                                  })
                                }
                                helperText="Sent as a JSON number — a numeric field compared against a string is a different comparison."
                              />
                            )}
                          </>
                        );
                      })()}
                      <PlanPicker
                        label="If true, move to (master)"
                        value={draft.ifPlan}
                        onChange={(v) => setDraft('ifPlan', v)}
                        planOptions={planOptions}
                        catalogFailed={catalogFailed}
                      />
                      <PlanPicker
                        label="If false, move to (master)"
                        value={draft.elsePlan}
                        onChange={(v) => setDraft('elsePlan', v)}
                        planOptions={planOptions}
                        catalogFailed={catalogFailed}
                      />
                    </Stack>
                  )}

                  {draft.kind === 'pay' && (
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="pay-doc">Document</InputLabel>
                        <Select
                          labelId="pay-doc"
                          label="Document"
                          value={draft.payDoc ?? PAY_DOCS[0]}
                          onChange={(e) => setDraft('payDoc', String(e.target.value))}
                        >
                          {PAY_DOCS.map((d) => (
                            <MenuItem key={d} value={d}>
                              {d}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Invoice ID"
                        value={draft.invoiceId ?? ''}
                        onChange={(e) => setDraft('invoiceId', e.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                        helperText="Required. public.invoice.id — a positive integer, not a UUID."
                      />
                    </Stack>
                  )}

                  {draft.kind === 'review' && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Review platform"
                        value={draft.reviewPlatform ?? ''}
                        onChange={(e) => setDraft('reviewPlatform', e.target.value)}
                        inputProps={{ maxLength: LEN_REF_128 }}
                        helperText="Free text, e.g. Google. Not a server-side enum."
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="Review request message"
                        value={draft.reviewMsg ?? ''}
                        onChange={(e) => setDraft('reviewMsg', e.target.value)}
                        inputProps={{ maxLength: LEN_BODY_2000 }}
                      />
                    </Stack>
                  )}

                  {draft.kind === 'notify' && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Notification title"
                        value={draft.notifyTitle ?? ''}
                        onChange={(e) => setDraft('notifyTitle', e.target.value)}
                        inputProps={{ maxLength: LEN_NAME_200 }}
                        helperText={`${(draft.notifyTitle ?? '').length}/${LEN_NAME_200}`}
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Notification body"
                        value={draft.notifyBody ?? ''}
                        onChange={(e) => setDraft('notifyBody', e.target.value)}
                        inputProps={{ maxLength: LEN_NOTIFY_BODY_240 }}
                        helperText={`${(draft.notifyBody ?? '').length}/${LEN_NOTIFY_BODY_240}`}
                      />
                    </Stack>
                  )}

                  {draft.kind === 'moveplan' && (
                    <Stack spacing={2}>
                      <PlanPicker
                        label="Move enrollment to plan (master)"
                        value={draft.targetPlan}
                        onChange={(v) => setDraft('targetPlan', v)}
                        allowNone={false}
                        planOptions={planOptions}
                        catalogFailed={catalogFailed}
                      />
                    </Stack>
                  )}

                  {draft.kind === 'wait' && (
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="wait-mode">Wait for</InputLabel>
                        <Select
                          labelId="wait-mode"
                          label="Wait for"
                          value={draft.waitMode ?? 'days'}
                          onChange={(e) => setDraft('waitMode', e.target.value as WaitMode)}
                        >
                          {WAIT_MODES.map((m) => (
                            <MenuItem key={m} value={m}>
                              {m === 'days' ? 'A number of days' : m === 'date' ? 'A date set per enrolment' : 'A customer response'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {(draft.waitMode ?? 'days') === 'days' && (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Days to wait"
                          value={draft.waitDays ?? ''}
                          onChange={(e) => setDraft('waitDays', e.target.value)}
                          inputProps={{ min: 1, step: 1 }}
                          helperText="Required, a positive whole number."
                        />
                      )}


                      {draft.waitMode === 'response' && (
                        <>
                          <TextField
                            fullWidth
                            size="small"
                            label="Response trigger"
                            value={draft.waitTrigger ?? ''}
                            onChange={(e) => setDraft('waitTrigger', e.target.value)}
                            inputProps={{ maxLength: LEN_NAME_200 }}
                          />
                          <Stack direction="row" spacing={1}>
                            <TextField
                              size="small"
                              type="number"
                              label="Window"
                              value={draft.waitWindowNum ?? ''}
                              onChange={(e) => setDraft('waitWindowNum', e.target.value)}
                              inputProps={{ min: 1, step: 1 }}
                              sx={{ width: 120 }}
                            />
                            <TextField
                              size="small"
                              label="Window unit"
                              value={draft.waitWindowUnit ?? ''}
                              onChange={(e) => setDraft('waitWindowUnit', e.target.value)}
                              inputProps={{ maxLength: LEN_STAGE_32 }}
                            />
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <FormControl size="small" fullWidth>
                              <InputLabel id="met-action">If they respond</InputLabel>
                              <Select
                                labelId="met-action"
                                label="If they respond"
                                value={draft.metAction ?? ''}
                                onChange={(e) => setDraft('metAction', e.target.value as WaitAction)}
                              >
                                <MenuItem value="">— Nothing —</MenuItem>
                                {WAIT_ACTIONS.map((a) => (
                                  <MenuItem key={a} value={a}>
                                    {a}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl size="small" fullWidth>
                              <InputLabel id="unmet-action">If they do not</InputLabel>
                              <Select
                                labelId="unmet-action"
                                label="If they do not"
                                value={draft.unmetAction ?? ''}
                                onChange={(e) => setDraft('unmetAction', e.target.value as WaitAction)}
                              >
                                <MenuItem value="">— Nothing —</MenuItem>
                                {WAIT_ACTIONS.map((a) => (
                                  <MenuItem key={a} value={a}>
                                    {a}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  )}
                </Paper>

                {stepProblem(draft) && (
                  <Alert severity="error" data-testid="step-problem">
                    {stepProblem(draft)}
                  </Alert>
                )}

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => setSelIdx(null)}
                    data-testid="save-action"
                  >
                    Save action
                  </Button>
                </Stack>
              </Stack>
            )}
          </Grid>
        </Grid>

        {problems.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="save-blocked">
            <AlertTitle>
              {problems.length} step{problems.length === 1 ? '' : 's'} cannot be saved yet
            </AlertTitle>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {problems.map((p) => (
                <Chip
                  key={p.index}
                  size="small"
                  color="warning"
                  label={`Step ${p.index + 1}: ${p.problem}`}
                  onClick={() => setSelIdx(p.index)}
                />
              ))}
            </Stack>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" data-testid="builder-footer-count">
          {plan.steps.length} action{plan.steps.length === 1 ? '' : 's'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={!canSave} onClick={save} data-testid="save-and-close">
            {busy ? 'Saving…' : 'Save & close'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default PlanMasterBuilder;
